import { BriefDeliverableStatusEnum } from "@entities/brief/lib/constants";
import { useApproveTaskContent } from "@entities/brief/lib/hooks/useApproveTaskContent";
import { useGetBrIefById } from "@entities/brief/lib/hooks/useGetBriefById";
import { useRequestEditTaskContent } from "@entities/brief/lib/hooks/useRequestEditTaskContent";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import { ToastService } from "@shared/lib/services";
import { BriefCampaignInf } from "@shared/lib/types/briefCampains";
import { IDraftingResponse } from "@shared/lib/types/drafting";
import { Button } from "@shared/ui/Button";
import { Flex, Space } from "antd";
import { Check, MessageSquare, UploadIcon } from "lucide-react";
import { useEffect } from "react";
import styles from "./DraftFooter.module.scss";

interface DraftFooterProps {
  draft?: IDraftingResponse;
  isLatestVersion: boolean;
  taskStatus?: BriefDeliverableStatusEnum;
  onSwitchToLatest: () => void;
  onUploadNewVersion: () => void;
  unresolvedCommentCount: number;
  briefId: string;
  campaignInfluencerId: string;
  deliverableConfigRefId: string;
  taskId: string;
  onRefetch: () => void;
  campaignInfluencer?: BriefCampaignInf;
}

export function DraftFooter({
  draft,
  isLatestVersion,
  taskStatus,
  onSwitchToLatest,
  onUploadNewVersion,
  unresolvedCommentCount,
  briefId,
  campaignInfluencerId,
  deliverableConfigRefId,
  taskId,
  onRefetch,
  campaignInfluencer,
}: DraftFooterProps) {
  const { userDetails } = useGetMe();
  const { brief } = useGetBrIefById(briefId);

  const isCancelledOrWithdrawn =
    campaignInfluencer?.isBrandDeclined ||
    campaignInfluencer?.isCreatorDeclined;
  const cancelledLabel = campaignInfluencer?.isBrandDeclined
    ? "Cancelled"
    : "Withdrawn";
  const cancelledReason =
    campaignInfluencer?.brandDeclinedReason ||
    campaignInfluencer?.creatorDeclinedReason;
  const { approveTaskContent, isApproving, isApproved } = useApproveTaskContent(
    briefId,
    () => {
      onRefetch();
    }
  );

  const { requestEditTaskContent, isRequestingEdit, isRequestedEdit } =
    useRequestEditTaskContent(briefId, () => {
      onRefetch();
    });

  useEffect(() => {
    if (isApproved) {
      ToastService.success("Draft approved successfully");
    }
  }, [isApproved]);

  useEffect(() => {
    if (isRequestedEdit) {
      ToastService.success("Changes requested successfully");
    }
  }, [isRequestedEdit]);

  const handleApprove = () => {
    if (!taskId) return;

    approveTaskContent({
      data: {
        campaignInfluencerId,
        deliverableConfigRefId,
        taskId,
      },
    });
  };

  const handleRequestChanges = () => {
    if (!taskId) return;

    requestEditTaskContent({
      data: {
        campaignInfluencerId,
        deliverableConfigRefId,
        taskId,
      },
    });
  };

  const renderCta = () => {
    switch (draft?.campaignInfluencerTaskState?.status) {
      case BriefDeliverableStatusEnum.Pending:
      case BriefDeliverableStatusEnum.EditsRequested:
        return (
          <Button
            onClick={() => onUploadNewVersion?.()}
            size="small"
            icon={<UploadIcon size={12} />}
            type="primary"
            style={{ backgroundColor: "rgb(79, 70, 229)" }}
          >
            Create New
          </Button>
        );
      case BriefDeliverableStatusEnum.PendingApproval:
        return (
          <>
            <Button
              onClick={() =>
                requestEditTaskContent({
                  data: {
                    campaignInfluencerId: draft?.campaignInfluencer?._id || "",
                    deliverableConfigRefId:
                      draft?.campaignInfluencerDeliverableState?._id || "",
                    taskId: draft?.campaignInfluencerTaskState?._id || "",
                  },
                })
              }
              disabled={
                !brief?.canInternalTeamTakeOpsActionsOnBehalfOfBrand &&
                !userDetails?.userEntity?.permissions?.includes("super")
              }
              loading={isRequestingEdit}
              icon={<MessageSquare size={14} />}
            >
              Request Edits
            </Button>
            <Button
              onClick={() =>
                approveTaskContent({
                  data: {
                    campaignInfluencerId: draft?.campaignInfluencer?._id || "",
                    deliverableConfigRefId:
                      draft?.campaignInfluencerDeliverableState?._id || "",
                    taskId: draft?.campaignInfluencerTaskState?._id || "",
                  },
                })
              }
              loading={isApproving}
              disabled={
                !brief?.canInternalTeamTakeOpsActionsOnBehalfOfBrand &&
                !userDetails?.userEntity?.permissions?.includes("super")
              }
              type={"primary"}
              icon={<Check size={14} />}
            >
              {isLastTask ? "Approve" : "Forward with Instructions"}
            </Button>
          </>
        );
      case BriefDeliverableStatusEnum.Completed:
        return (
          <Button type="dashed" disabled>
            Draft Completed
          </Button>
        );
      default:
        return null;
    }
  };
  const selectedDeliverableConfig = draft?.briefDeliverableConfig.find(
    (d) =>
      d.deliverableConfigRefId ===
      draft?.campaignInfluencerDeliverableState?._id
  );

  const selectedDeliverableConfigTask = selectedDeliverableConfig?.tasks.find(
    (t) => t.taskId === draft?.campaignInfluencerTaskState?._id
  );

  const configCurrentTaskApprovalOn =
    selectedDeliverableConfigTask?.approvalType.at(-1);

  const isLastTask =
    configCurrentTaskApprovalOn ===
    draft?.campaignInfluencerTaskState?.approvalPendingOn;
  return (
    <Flex justify="flex-end" style={{ margin: 10 }}>
      <Space size={10} wrap className={styles.actions}>
        {isCancelledOrWithdrawn ? (
          <div className={`${styles.draft} ${styles.draftCancelled}`}>
            {/* <div className={styles.draftFooterLeft} /> */}
            <div className={styles.draftRight}>
              <span className={styles.draftMessage}>
                This Creator has been {cancelledLabel}
                {cancelledReason ? (
                  <>
                    :{" "}
                    <span className={styles.draftMessageReason}>
                      {cancelledReason}
                    </span>
                  </>
                ) : null}
              </span>
            </div>
          </div>
        ) : isLatestVersion ? (
          renderCta()
        ) : null}
      </Space>
    </Flex>
  );
}
